import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Home, Leaf, ListChecks, BookOpen, Sparkles,
  Camera, MapPin, CloudSun, Menu, X,
} from 'lucide-react';

// Shared palette. A couple of shades are spec-driven and do not live in the
// Tailwind theme, so we inline them rather than pretending they are tokens.
const CREAM = '#f5f2eb';
const DARK  = '#2c3e1f';
const INK   = '#1a2410';
const SAGE  = '#9ab87a';
const GREEN = '#3B6D11';
const SOFT_GREEN = '#EAF3DE';

const SERIF = { fontFamily: 'Georgia, "Times New Roman", serif' };

// -------------------------------------------------------------------------
// Nav
// -------------------------------------------------------------------------

const PRIMARY_LINKS = [
  { to: '/garden',  label: 'My garden' },
  { to: '/ideas',   label: 'Get yard ideas' },
];

// "For designers" enters the app through the existing sign-in route. The
// find-a-designer destination is /find-designer; only the label changes from
// the old "Find a Designer Near Me" to the friendlier "Work with a designer".
const SECONDARY_LINKS = [
  { to: '/login',          label: 'For designers' },
  { to: '/find-designer',  label: 'Work with a designer' },
];

function TopNav() {
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <header
      className="sticky top-0 z-40 border-b"
      style={{ background: CREAM, borderColor: 'rgba(0,0,0,0.06)' }}
    >
      <div className="mx-auto max-w-6xl px-4 sm:px-6 h-14 flex items-center gap-4">
        <Link to="/" className="flex items-center shrink-0" aria-label="PlantaDay home">
          <img
            src="/plantadaylogo.png"
            alt="PlantaDay"
            className="h-8 w-auto"
            draggable={false}
          />
        </Link>

        {/* Desktop links */}
        <nav className="hidden md:flex items-center gap-1 flex-1">
          <div className="flex items-center gap-1">
            {PRIMARY_LINKS.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                className="px-3 py-2 rounded-md transition hover:bg-black/5"
                style={{ fontSize: 13, color: '#555' }}
              >
                {l.label}
              </Link>
            ))}
          </div>

          <span
            aria-hidden
            className="mx-2 h-5 w-px"
            style={{ background: 'rgba(0,0,0,0.12)' }}
          />

          <div className="flex items-center gap-1">
            {SECONDARY_LINKS.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                className="px-3 py-2 rounded-md transition hover:bg-black/5"
                style={{ fontSize: 12, color: '#aaa', fontWeight: 400 }}
              >
                {l.label}
              </Link>
            ))}
          </div>
        </nav>

        <div className="flex-1 md:hidden" />

        <Link
          to="/create-account"
          state={{ accountType: 'customer' }}
          className="hidden sm:inline-flex items-center justify-center rounded-full px-4 h-9 text-[13px] font-medium text-white transition active:scale-95"
          style={{ background: GREEN }}
        >
          Get started free
        </Link>

        <button
          type="button"
          onClick={() => setMenuOpen((v) => !v)}
          className="md:hidden min-h-10 min-w-10 rounded-md flex items-center justify-center"
          style={{ color: INK }}
          aria-label={menuOpen ? 'Close menu' : 'Open menu'}
          aria-expanded={menuOpen}
        >
          {menuOpen ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {menuOpen ? (
        <div className="md:hidden border-t" style={{ borderColor: 'rgba(0,0,0,0.06)' }}>
          <div className="mx-auto max-w-6xl px-4 py-3 flex flex-col">
            {PRIMARY_LINKS.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                onClick={() => setMenuOpen(false)}
                className="py-2.5 rounded-md px-2 -mx-2 hover:bg-black/5"
                style={{ fontSize: 15, color: '#333' }}
              >
                {l.label}
              </Link>
            ))}

            <div className="mt-3 mb-1 text-[11px] uppercase tracking-wide" style={{ color: '#aaa' }}>
              For professionals
            </div>
            {SECONDARY_LINKS.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                onClick={() => setMenuOpen(false)}
                className="py-2.5 rounded-md px-2 -mx-2 hover:bg-black/5"
                style={{ fontSize: 13, color: '#999', fontWeight: 400 }}
              >
                {l.label}
              </Link>
            ))}

            <Link
              to="/create-account"
              state={{ accountType: 'customer' }}
              onClick={() => setMenuOpen(false)}
              className="mt-3 inline-flex items-center justify-center rounded-full px-4 h-11 text-sm font-medium text-white"
              style={{ background: GREEN }}
            >
              Get started free
            </Link>
          </div>
        </div>
      ) : null}
    </header>
  );
}

// -------------------------------------------------------------------------
// Hero
// -------------------------------------------------------------------------

function Hero() {
  return (
    <section className="px-4 sm:px-6 pt-14 sm:pt-20 pb-6 text-center">
      <div className="mx-auto max-w-3xl">
        <div
          className="text-[11px] sm:text-xs uppercase tracking-[0.18em] mb-4"
          style={{ color: GREEN }}
        >
          Your real garden, one plant at a time
        </div>
        <h1
          className="text-4xl sm:text-5xl md:text-6xl leading-[1.1] font-semibold"
          style={{ ...SERIF, color: INK }}
        >
          Know your garden.
          <br />
          <span className="italic font-normal" style={{ color: GREEN }}>
            Season by season.
          </span>
        </h1>
        <p
          className="mx-auto mt-5 max-w-xl text-base sm:text-lg leading-relaxed"
          style={{ color: '#4a5740' }}
        >
          Photo your garden areas, place your plants, track what blooms when,
          and get a simple list of what to do next. No expertise needed.
        </p>

        <div className="mt-7 flex flex-wrap items-center justify-center gap-3">
          <Link
            to="/create-account"
            state={{ accountType: 'customer' }}
            className="inline-flex items-center justify-center rounded-full px-6 h-12 text-sm sm:text-[15px] font-medium text-white transition active:scale-95"
            style={{ background: GREEN }}
          >
            Start my garden {'\u2014'} it{'\u2019'}s free
          </Link>
          <a
            href="#how-it-works"
            className="inline-flex items-center justify-center rounded-full px-6 h-12 text-sm sm:text-[15px] font-medium transition hover:bg-black/5"
            style={{ color: GREEN, border: `1.5px solid ${GREEN}` }}
          >
            See how it works
          </a>
        </div>
      </div>
    </section>
  );
}

// -------------------------------------------------------------------------
// App preview (phone frame + faithful zone grid)
// -------------------------------------------------------------------------

// Mock zone data. The three photo backgrounds are CSS gradients rather than
// real images so the page has no external asset dependency. Each gradient is
// tuned to read as an outdoor garden scene at a glance.
const PREVIEW_ZONES = [
  {
    name: 'Front border',
    season: 'Summer',
    plants: 8,
    image: '/landing-previews/zone-front-border.webp',
  },
  {
    name: 'Back fence',
    season: 'Spring',
    plants: 5,
    image: '/landing-previews/zone-back-fence.webp',
  },
  {
    name: 'Raised bed',
    season: 'Fall',
    plants: 3,
    image: '/landing-previews/zone-raised-bed.webp',
  },
];

const PREVIEW_TABS = [
  { Icon: Home,       label: 'Garden',  active: true },
  { Icon: Leaf,       label: 'Plants' },
  { Icon: ListChecks, label: 'Tasks',   badge: 3 },
  { Icon: BookOpen,   label: 'Journal' },
  { Icon: Sparkles,   label: 'AI Help' },
];

function MockZoneCard({ zone }) {
  return (
    <div
      className="rounded-2xl overflow-hidden bg-white shadow-sm"
      style={{ border: '1px solid rgba(0,0,0,0.06)' }}
    >
      <div className="relative aspect-[4/5]">
        <img
          src={zone.image}
          alt={`${zone.name} — ${zone.season}`}
          className="absolute inset-0 w-full h-full object-cover"
          loading="lazy"
          decoding="async"
        />
        <span
          className="absolute top-2 left-2 inline-flex items-center px-2 h-5 rounded-full bg-white/90 text-[10px] font-medium"
          style={{ color: INK }}
        >
          {zone.season}
        </span>
        <span
          className="absolute bottom-2 right-2 inline-flex items-center justify-center min-w-[22px] h-5 px-1.5 rounded-full text-[10px] font-semibold text-white"
          style={{ background: GREEN }}
        >
          {zone.plants}
        </span>
      </div>
      <div className="px-3 py-2">
        <div className="text-[13px] font-semibold truncate" style={{ color: INK }}>
          {zone.name}
        </div>
      </div>
    </div>
  );
}

function MockTab({ Icon, label, active, badge }) {
  return (
    <div
      className="min-h-[56px] flex flex-col items-center justify-center gap-0.5 text-[11px] font-medium"
      style={{ color: active ? GREEN : '#6b7a5e' }}
    >
      <span className="relative inline-flex">
        <Icon size={20} strokeWidth={2} />
        {badge ? (
          <span
            className="absolute -top-1.5 -right-2 min-w-[16px] h-[16px] px-1 rounded-full text-white text-[10px] font-semibold leading-[16px] text-center"
            style={{ background: GREEN }}
          >
            {badge}
          </span>
        ) : null}
      </span>
      <span>{label}</span>
    </div>
  );
}

function AppPreview() {
  return (
    <section className="px-4 sm:px-6 pb-16">
      <div
        className="mx-auto w-full relative overflow-hidden"
        style={{
          maxWidth: 420,
          borderRadius: 28,
          border: '2px solid #d8d4c8',
          background: '#fbfaf6',
          boxShadow: '0 20px 40px -20px rgba(26, 36, 16, 0.25), 0 2px 8px -2px rgba(26, 36, 16, 0.08)',
        }}
      >
        <div
          aria-hidden
          className="h-6 flex items-center justify-center"
          style={{ background: '#fbfaf6' }}
        >
          <span className="w-14 h-1 rounded-full" style={{ background: 'rgba(0,0,0,0.12)' }} />
        </div>

        <div className="flex items-center justify-between px-4 pt-2 pb-2">
          <h3 className="text-[17px] font-semibold" style={{ color: INK }}>
            My garden
          </h3>
        </div>

        <div className="grid grid-cols-2 gap-3 px-4 pb-4">
          {PREVIEW_ZONES.map((z) => (
            <MockZoneCard key={z.name} zone={z} />
          ))}
        </div>

        <div
          className="border-t"
          style={{ borderColor: 'rgba(0,0,0,0.06)', background: 'rgba(255,255,255,0.95)' }}
        >
          <div className="grid grid-cols-5">
            {PREVIEW_TABS.map((t) => <MockTab key={t.label} {...t} />)}
          </div>
        </div>
      </div>
    </section>
  );
}

// -------------------------------------------------------------------------
// Dark band: memory statement
// -------------------------------------------------------------------------

function MemoryBand() {
  return (
    <section className="py-16 sm:py-20 px-4 sm:px-6" style={{ background: DARK }}>
      <div className="mx-auto max-w-3xl text-center">
        <h2
          className="text-3xl sm:text-4xl md:text-5xl leading-tight font-semibold text-white"
          style={SERIF}
        >
          Your garden has a memory now.
        </h2>
        <p className="mt-4 text-base sm:text-lg leading-relaxed" style={{ color: SAGE }}>
          Stop trying to remember what you planted, where it is, and what it
          needs. PlantaDay keeps the record. You just take the photos.
        </p>
      </div>
    </section>
  );
}

// -------------------------------------------------------------------------
// Features
// -------------------------------------------------------------------------

const FEATURES = [
  {
    Icon: Camera,
    title: 'Photo your garden areas',
    body: 'No aerial maps needed. Just take a photo of any bed, border, or patio from where you are standing. Each area becomes a zone with its own plants and history.',
  },
  {
    Icon: MapPin,
    title: 'Tap to place your plants',
    body: 'Tap on the photo where a plant lives and assign it from your library. Plant markers stay put so you always know what is planted where, even seasons later.',
  },
  {
    Icon: CloudSun,
    title: 'Track through the seasons',
    body: 'Add a photo each season so you can see how your garden changes. Spring blooms, summer fullness, autumn colour, winter structure, all in one place.',
  },
  {
    Icon: ListChecks,
    title: 'Know what to do next',
    body: 'PlantaDay reads your plant care info and builds your task list automatically. Prune the roses this week. Fertilise the lawn in May. No guesswork.',
  },
  {
    Icon: Sparkles,
    title: 'AI help when you need it',
    body: 'Ask anything. What is wrong with this leaf? When should I divide my hostas? What can I plant in this shady corner? Your garden AI knows your plants.',
  },
];

function FeaturesSection() {
  return (
    <section className="py-16 sm:py-24 px-4 sm:px-6" style={{ background: CREAM }}>
      <div className="mx-auto max-w-3xl">
        <div
          className="text-[11px] uppercase tracking-[0.18em] mb-3 text-center"
          style={{ color: GREEN }}
        >
          What it does
        </div>
        <h2
          className="text-3xl sm:text-4xl font-semibold text-center leading-tight"
          style={{ ...SERIF, color: INK }}
        >
          Everything your garden needs you to remember.
        </h2>

        <ul className="mt-10 sm:mt-14 space-y-8 sm:space-y-10">
          {FEATURES.map(({ Icon, title, body }) => (
            <li key={title} className="flex gap-4 sm:gap-6 items-start">
              <div
                className="shrink-0 w-12 h-12 sm:w-14 sm:h-14 rounded-2xl flex items-center justify-center"
                style={{ background: SOFT_GREEN, color: GREEN }}
              >
                <Icon size={22} strokeWidth={2} />
              </div>
              <div className="min-w-0">
                <h3
                  className="text-lg sm:text-xl font-semibold mb-1"
                  style={{ color: INK }}
                >
                  {title}
                </h3>
                <p className="text-sm sm:text-base leading-relaxed" style={{ color: '#4a5740' }}>
                  {body}
                </p>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

// -------------------------------------------------------------------------
// Seasons grid
// -------------------------------------------------------------------------

const SEASONS = [
  {
    name: 'Spring',
    blurb: 'new growth & planting',
    image: '/landing-previews/season-spring.webp',
    pill: 'rgba(255,255,255,0.9)',
    pillText: '#2a5a08',
    text: '#ffffff',
  },
  {
    name: 'Summer',
    blurb: 'bloom & maintenance',
    image: '/landing-previews/season-summer.webp',
    pill: 'rgba(255,255,255,0.9)',
    pillText: '#1f5a0f',
    text: '#ffffff',
  },
  {
    name: 'Fall',
    blurb: 'prep & wind-down',
    image: '/landing-previews/season-fall.webp',
    pill: 'rgba(255,255,255,0.9)',
    pillText: '#7a3d0a',
    text: '#ffffff',
  },
  {
    name: 'Winter',
    blurb: 'structure & planning',
    image: '/landing-previews/season-winter.webp',
    pill: 'rgba(255,255,255,0.9)',
    pillText: '#3a4a58',
    text: '#ffffff',
  },
];

function SeasonsSection() {
  return (
    <section className="py-16 sm:py-24 px-4 sm:px-6" style={{ background: CREAM }}>
      <div className="mx-auto max-w-5xl">
        <div className="text-center mb-10 sm:mb-14">
          <h2
            className="text-3xl sm:text-4xl font-semibold leading-tight"
            style={{ ...SERIF, color: INK }}
          >
            One garden, four seasons.
          </h2>
          <p
            className="mx-auto mt-4 max-w-2xl text-base sm:text-lg leading-relaxed"
            style={{ color: '#4a5740' }}
          >
            Each area stores a photo for every season, so you can see exactly
            how your garden looks and what it needs throughout the year.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
          {SEASONS.map((s) => (
            <div
              key={s.name}
              className="relative rounded-3xl overflow-hidden min-h-[180px] sm:min-h-[220px]"
              style={{ color: s.text }}
            >
              <img
                src={s.image}
                alt=""
                aria-hidden="true"
                className="absolute inset-0 w-full h-full object-cover"
                loading="lazy"
                decoding="async"
              />
              <div
                className="absolute inset-0"
                style={{
                  background:
                    'linear-gradient(180deg, rgba(0,0,0,0.05) 0%, rgba(0,0,0,0.15) 45%, rgba(0,0,0,0.55) 100%)',
                }}
              />
              <div className="relative h-full p-6 sm:p-8 flex flex-col justify-end min-h-[180px] sm:min-h-[220px]">
                <span
                  className="inline-flex self-start items-center px-2.5 h-6 rounded-full text-[11px] font-semibold uppercase tracking-wider mb-3"
                  style={{ background: s.pill, color: s.pillText }}
                >
                  {s.name}
                </span>
                <p className="text-lg sm:text-xl leading-snug" style={{ ...SERIF, textShadow: '0 1px 2px rgba(0,0,0,0.4)' }}>
                  {s.blurb}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// -------------------------------------------------------------------------
// How it works (dark band)
// -------------------------------------------------------------------------

const STEPS = [
  {
    title: 'Add a garden area',
    body: 'Give it a name and take a photo from wherever you are standing.',
  },
  {
    title: 'Place your plants',
    body: 'Tap the photo where each plant lives. Add from your library or let AI identify it.',
  },
  {
    title: 'Get your task list',
    body: 'PlantaDay reads your plants care needs and tells you what to do this week and this season.',
  },
  {
    title: 'Update each season',
    body: 'Snap a new photo as your garden changes. Your record builds over time.',
  },
];

function HowItWorks() {
  return (
    <section
      id="how-it-works"
      className="py-16 sm:py-24 px-4 sm:px-6"
      style={{ background: DARK }}
    >
      <div className="mx-auto max-w-4xl">
        <div className="text-center mb-12 sm:mb-16">
          <h2
            className="text-3xl sm:text-4xl md:text-5xl font-semibold text-white leading-tight"
            style={SERIF}
          >
            Up and running in minutes.
          </h2>
          <p className="mt-4 text-base sm:text-lg" style={{ color: SAGE }}>
            No setup. No learning curve. Just your garden.
          </p>
        </div>

        <ol className="grid grid-cols-1 sm:grid-cols-2 gap-6 sm:gap-8">
          {STEPS.map((s, i) => (
            <li key={s.title} className="flex gap-4">
              <div
                className="shrink-0 w-10 h-10 rounded-full flex items-center justify-center text-white font-semibold"
                style={{ background: GREEN }}
              >
                {i + 1}
              </div>
              <div>
                <h3 className="text-lg sm:text-xl font-semibold text-white mb-1">
                  {s.title}
                </h3>
                <p className="text-sm sm:text-base leading-relaxed" style={{ color: SAGE }}>
                  {s.body}
                </p>
              </div>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}

// -------------------------------------------------------------------------
// Secondary professional section
// -------------------------------------------------------------------------

function ProSection() {
  return (
    <section className="py-14 sm:py-20 px-4 sm:px-6" style={{ background: CREAM }}>
      <div className="mx-auto max-w-3xl text-center">
        <div
          className="text-[11px] uppercase tracking-[0.18em] mb-3"
          style={{ color: '#9a9a9a' }}
        >
          For professionals
        </div>
        <h2
          className="text-2xl sm:text-3xl font-semibold leading-tight"
          style={{ ...SERIF, color: '#3a4532' }}
        >
          Are you a landscape designer?
        </h2>
        <p className="mt-3 text-sm sm:text-base leading-relaxed" style={{ color: '#6b7a5e' }}>
          PlantaDay also has a full professional design suite, plus a directory
          to help homeowners find designers near them.
        </p>

        <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
          <Link
            to="/login"
            className="inline-flex items-center gap-1.5 rounded-full px-5 h-10 text-[13px] font-medium transition hover:bg-black/5"
            style={{ color: '#6b7a5e', border: '1px solid rgba(0,0,0,0.15)' }}
          >
            For designers {'\u2192'}
          </Link>
          <Link
            to="/ideas"
            className="inline-flex items-center gap-1.5 rounded-full px-5 h-10 text-[13px] font-medium transition hover:bg-black/5"
            style={{ color: '#6b7a5e', border: '1px solid rgba(0,0,0,0.15)' }}
          >
            Get yard ideas {'\u2192'}
          </Link>
        </div>
      </div>
    </section>
  );
}

// -------------------------------------------------------------------------
// Final CTA + footer
// -------------------------------------------------------------------------

function FinalCTA() {
  return (
    <section className="py-20 sm:py-28 px-4 sm:px-6" style={{ background: CREAM }}>
      <div className="mx-auto max-w-3xl text-center">
        <h2
          className="text-3xl sm:text-4xl md:text-5xl font-semibold leading-tight"
          style={{ ...SERIF, color: INK }}
        >
          Your garden deserves a better memory.
        </h2>
        <p className="mt-4 text-base sm:text-lg" style={{ color: '#4a5740' }}>
          Free to start. No credit card. Works on any phone.
        </p>
        <div className="mt-7">
          <Link
            to="/create-account"
            state={{ accountType: 'customer' }}
            className="inline-flex items-center justify-center rounded-full px-7 h-12 text-sm sm:text-[15px] font-medium text-white transition active:scale-95"
            style={{ background: GREEN }}
          >
            Start my garden
          </Link>
        </div>
      </div>
    </section>
  );
}

function FooterBar() {
  const items = [
    { to: '/ideas',          label: 'Get yard ideas' },
    { to: '/find-designer',  label: 'Work with a designer' },
    { to: '/login',          label: 'For designers' },
    { to: '/privacy',        label: 'Privacy' },
  ];
  return (
    <footer
      className="px-4 sm:px-6 py-6 border-t"
      style={{ background: CREAM, borderColor: 'rgba(0,0,0,0.08)' }}
    >
      <div className="mx-auto max-w-6xl flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6">
        <Link to="/" className="flex items-center" aria-label="PlantaDay home">
          <img src="/plantadaylogo.png" alt="PlantaDay" className="h-7 w-auto" draggable={false} />
        </Link>

        <nav className="flex flex-wrap items-center gap-x-4 gap-y-2 sm:ml-auto">
          {items.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              className="text-[12px] hover:underline"
              style={{ color: '#9a9a9a' }}
            >
              {l.label}
            </Link>
          ))}
          <span className="text-[12px]" style={{ color: '#9a9a9a' }}>
            {'\u00a9'} 2026 PlantaDay
          </span>
        </nav>
      </div>
    </footer>
  );
}

// -------------------------------------------------------------------------
// Root
// -------------------------------------------------------------------------

export default function GardenerHome() {
  return (
    <div style={{ background: CREAM, color: INK }} className="min-h-screen">
      <TopNav />
      <main>
        <Hero />
        <AppPreview />
        <MemoryBand />
        <FeaturesSection />
        <SeasonsSection />
        <HowItWorks />
        <ProSection />
        <FinalCTA />
      </main>
      <FooterBar />
    </div>
  );
}
